import { BaseVideoPlayer } from '../players/BaseVideoPlayer'
import { LoggerName, namedLogger } from '../../../../../../utils/logger'

const logger = namedLogger(LoggerName.VideoMirror)

export class VideoMirror {
  private readonly name: string
  private currentCanvas: HTMLCanvasElement | undefined
  private currentlyMirroredVideo: BaseVideoPlayer | null = null
  private outstandingDrawFrameRequests = new Map<BaseVideoPlayer, { count: number }>()

  constructor(name: string) {
    this.name = name
  }

  private isVideoReadyToDraw(video: HTMLVideoElement) {
    return video.readyState >= 2
  }

  /**
   * Makes sure we have a canvas in place.
   * @param videoMirrorPlaceholder
   */
  public initializeCanvas(videoMirrorPlaceholder: HTMLElement) {
    logger.debug('VideoMirror - initializeCanvas for', this.name)
    const newCanvas = videoMirrorPlaceholder.ownerDocument.createElement('canvas')
    newCanvas.style.objectFit = 'contain'
    newCanvas.style.minWidth = '0'
    newCanvas.style.minHeight = '0'
    newCanvas.style.width = '100%'
    newCanvas.style.height = '100%'
    newCanvas.style.objectPosition = 'center center'
    newCanvas.style.background = 'black'
    videoMirrorPlaceholder.replaceChildren(newCanvas)
    this.currentCanvas = newCanvas
    return newCanvas
  }

  /**
   * Inserts a new canvas into the placeholder and starts mirroring if mirroring was started previously.
   * @param video
   * @param shouldBeMirroring
   */
  public reestablishMirrorInPlaceholder(video: BaseVideoPlayer, shouldBeMirroring: boolean) {
    logger.debug('VideoMirror - reestablishMirrorInPlaceholder', video.getVideoIdentifier(), 'to', this.name)

    if (shouldBeMirroring) {
      this.startMirroring(video, true)
    } else {
      this.stopMirroring()
    }
  }

  /**
   * Starts mirroring the given video player's video element to the canvas associated with this mirror.
   * This may be called before or after reestablishMirrorInPlaceholder is called, so it needs to be able to handle the case where the canvas is not
   * yet present in the DOM. If the canvas is not present, it will start mirroring once reestablishMirrorInPlaceholder is called.
   * @param video
   * @param cavasJustAdded
   */
  public startMirroring = (video: BaseVideoPlayer, cavasJustAdded = false) => {
    logger.debug('VideoMirror - startMirroring', video.getVideoIdentifier(), 'to', this.name)

    // track the video that we are currently mirroring
    this.currentlyMirroredVideo = video

    // get the outstandingDrawFrameRequests record and initialize if not there yet.
    let drawFrameRequests = this.outstandingDrawFrameRequests.get(video)
    if (!drawFrameRequests) {
      drawFrameRequests = { count: 0 }
      this.outstandingDrawFrameRequests.set(video, drawFrameRequests)
    }

    const currentVideoElement = video.getVideoElement()
    if (!currentVideoElement) {
      logger.debug(`VideoMirror - startMirroring - no video element for ${video.getVideoIdentifier()}, skipping mirror setup`)
      return
    }

    const canvas = this.currentCanvas

    if (canvas) {
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        logger.debug(`VideoMirror - startMirroring - Could not get canvas context for mirroring ${video.getVideoIdentifier()} to ${this.name}`)
        return
      }

      const startDrawing = () => {
        logger.debug('VideoMirror - startDrawing', video.getVideoIdentifier(), 'to', this.name)

        const { videoWidth, videoHeight } = currentVideoElement
        canvas.width = videoWidth
        canvas.height = videoHeight

        const drawFrame = () => {
          // if everything is working as expected outstandingDrawFrameRequests will be 1
          if (drawFrameRequests.count > 0) {
            drawFrameRequests.count--
          }

          // if everything is working as expected outstandingDrawFrameRequests should now be 0
          if (drawFrameRequests.count > 0) {
            logger.debug('VideoMirror - duplicate request to mirror video, so aborting -', video.getVideoIdentifier(), 'to', this.name)
            return
          }

          // we will only continue drawing this video for as long as currentlyMirroredVideo matches the video we've been asked to draw
          if (video !== this.currentlyMirroredVideo) {
            // abort request
            return
          }

          if ((canvas.isConnected || cavasJustAdded) && this.isVideoReadyToDraw(currentVideoElement)) {
            ctx.drawImage(currentVideoElement, 0, 0, videoWidth, videoHeight)
          } else {
            logger.debug(
              'VideoMirror - did not draw frame - canvas.isConnected',
              canvas.isConnected,
              'isVideoReadyToDraw',
              this.isVideoReadyToDraw(currentVideoElement),
              'for',
              video.getVideoIdentifier(),
              'to',
              this.name
            )
          }

          const renderNextFrame = () => {
            if (canvas.isConnected) {
              // increment drawFrameRequests when we queue request the next frame
              drawFrameRequests.count++
              currentVideoElement.requestVideoFrameCallback(drawFrame)
            } else {
              logger.debug(
                'VideoMirror - renderNextFrame - stopping mirror because canvas is not connected -',
                video.getVideoIdentifier(),
                'to',
                this.name
              )
            }
          }

          if (cavasJustAdded) {
            requestAnimationFrame(renderNextFrame)
          } else {
            renderNextFrame()
          }
        }

        drawFrame()
      }

      if (this.isVideoReadyToDraw(currentVideoElement)) {
        startDrawing()
      } else {
        logger.debug('VideoMirror - waiting for canplay event to startDrawing', video.getVideoIdentifier(), 'to', this.name)
        currentVideoElement.addEventListener('canplay', startDrawing, { once: true })
      }
    }
  }

  public stopMirroring() {
    logger.debug('VideoMirror - stop mirroring')
    this.currentlyMirroredVideo = null

    // clear the canvas - I know this looks weird, but setting the height and width on the canvas clears it per MDN specs.
    if (this.currentCanvas) {
      this.currentCanvas.width = this.currentCanvas.width
      this.currentCanvas.height = this.currentCanvas.height
    }
  }
}
